import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { Dropdown } from 'semantic-ui-react';

import {
	handleError,
	log
} from '../../../../services/utils';

import {
	getTagList,
	deleteTag
} from '../../../../api/nora/labels';

import { useAuthState } from '../../../../contexts/AuthContext';
import { useUIAction } from '../../../../contexts/UIContext';
import {
	useTagState,
	useTagAction
} from '../../../../contexts/TagsContext';

import Alert from '../../../Alert';
import HelpModal from '../../../Shared/HelpModal';
import PageHeader from '../../../Shared/PageHeader';
import Table from '../../../Table';
import TableRowActions from '../../../Table/Cells/TableRowActions';

const TagList = () => {
	const { user: { permissions } } = useAuthState();
	const {
		initialized,
		tagList,
		tagTypes
	} = useTagState();
	const { setTagList } = useTagAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setTag,
		setOnTagModalClose
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ tagToDelete, setTagToDelete ] = useState( {} );
	const [ typeId, setTypeId ] = useState();

	useEffect( () => {
		setInfobar( <span>Administration &nbsp;&#9656;&nbsp; Tags</span> );
	}, [ setInfobar ] );

	useEffect( () => {
		if ( initialized ) {
			setTypeId( tagTypes[ 0 ].value );
		}
	}, [ initialized, tagTypes ] );

	const retrieveTagList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Tags...' );

		try {
			if ( typeId ) {
				const data = await getTagList( typeId );

				setTagList( data );
			}
		} catch ( error ) {
			setApiErrorAlert( error );
			throw error;
		}
	}, [
		setApiErrorAlert,
		setInfobarLoader,
		setTagList,
		typeId
	] );

	useEffect( () => {
		( async () => {
			if ( !initialized ) {
				setInfobarLoader( 'Please wait...' );
			} else {
				if ( permissions.listTags.canExecute && permissions.listTags.isAvailable ) {
					try {
						await retrieveTagList();
						setInfobarLoader( '' );
					} catch ( error ) {
						if ( error.message !== 'canceled' ) {
							handleError( error );
							setInfobarLoader( '' );
						}
					}
				} else {
					setInfobarLoader( '' );
				}

				setIsLoading( false );
			}
		} )();
	}, [
		initialized,
		permissions,
		retrieveTagList,
		setInfobarLoader
	] );

	const onView = useCallback( row => {
		setTag( row );
		setOnTagModalClose( retrieveTagList );
	}, [
		retrieveTagList,
		setTag,
		setOnTagModalClose
	] );

	const onDelete = useCallback( tag => {
		setTagToDelete( tag );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.editTags.canExecute,
				icon: 'search',
				permission: permissions.editTags.isAvailable,
				text: 'View Tag'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.deleteTags.canExecute,
				icon: 'times',
				permission: permissions.deleteTags.isAvailable,
				text: 'Delete Tag'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onView,
		permissions,
		onDelete
	] );

	const columns = useMemo( () => [
		{
			id: 'text',
			accessor: row => `${ row.text }`,
			Header: 'Name',
			filter: 'text',
			minWidth: 60
		}
	], [] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.editTags.isAvailable || permissions.deleteTags.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const addTag = useCallback( () => {
		setTag();
		setOnTagModalClose( retrieveTagList );
	}, [
		retrieveTagList,
		setTag,
		setOnTagModalClose
	] );

	const actions = useMemo( () => [
		{
			action: addTag,
			disable: !permissions.addTags.canExecute,
			icon: 'add',
			permission: permissions.addTags.isAvailable,
			text: 'Add Tag'
		}
	], [
		addTag,
		permissions
	] );

	const handleTagTypeChange = useCallback( ( e, { value } ) => {
		setTypeId( value );
	}, [] );

	const handledeleteTag = useCallback( async () => {
		try {
			await deleteTag( tagToDelete.id );
			await retrieveTagList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setIsLoading( false );
	}, [
		tagToDelete.id,
		retrieveTagList,
		setApiErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to delete <strong>{ tagToDelete.text }</strong> from the list?</span>, [ tagToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="page">
			{ log( 'tagAdmin' ) }
			<PageHeader
				actions={ actions }
				title={
					<>
						<span>Tag Management</span>
						<HelpModal
							id={ 37 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			<Dropdown
				options={ tagTypes }
				onChange={ handleTagTypeChange }
				selection
				style={ { marginBottom: '1rem' } }
				value={ typeId }
			/>
			<Table
				columns={ renderColumns }
				data={ tagList || [] }
				hasPermission={ permissions.listTags.canExecute }
			/>
			<Alert
				action={ handledeleteTag }
				name="tag-list-delete-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</div>
	);
};

export default memo( TagList );
